/**
 * MASTER 02 — EDITORIAL_STORY (Редакционная история).
 *
 * Long-read editorial spread: photo + editorial text.
 * Reuses HeroMaster tokens (palette, background) and CropModal.
 * Variants: A (photo left / text right), B (text left / photo right).
 */
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Upload, Image as ImageIcon, Crop, Plus, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import EditorialPilotDialog from "@/components/editorial/EditorialPilotDialog";
import { HERO_PALETTES, HERO_BACKGROUNDS } from "@/components/editorial/HeroMaster";
import { isImageMedia, mediaRecordUrl } from "@/lib/editorialMedia.mjs";
import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { editorialImageStyle, hasExplicitEditorialCrop } from "@/lib/editorialReaderLayout.mjs";
import { structureEditorialStory } from "@/lib/editorialSimplePilot.mjs";
import { EditorialStoryFormattedParagraphV1 } from "../editorial-v2/EditorialStoryFormattedParagraphV1";

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const authHeaders = () => {
  const t = localStorage.getItem("anokina_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const paletteByKey = (k) => HERO_PALETTES.find((p) => p.key === k) || HERO_PALETTES[2]; // olive
const backgroundByKey = (k) => HERO_BACKGROUNDS.find((b) => b.key === k) || HERO_BACKGROUNDS[0];

async function uploadDirect(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API_ROOT}/api/admin/media/upload-file`, fd, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

// ---------- Media library picker (compact reuse) ----------
function AuthenticatedEditorialImage({ url, ...props }) {
  const src = useEditorialMediaUrl(url);
  return src ? <img src={src} {...props} /> : null;
}

function MediaPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  if (open && !loaded) {
    axios.get(`${API_ROOT}/api/admin/media`, { headers: authHeaders() })
      .then((r) => setItems((r.data || []).filter((m) => isImageMedia(m) && mediaRecordUrl(m))))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[55] bg-black/50 flex items-center justify-center px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="story-media-picker">
      <div className="w-full max-w-3xl max-h-[80vh] bg-cream rounded-2xl overflow-hidden flex flex-col">
        <div className="p-4 border-b border-espresso/10 flex items-center justify-between">
          <div className="font-serif text-lg text-espresso">Медиатека</div>
          <button className="text-sm opacity-70" onClick={onClose}>Закрыть</button>
        </div>
        <div className="p-4 overflow-y-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((m) => {
            const src = mediaRecordUrl(m);
            return (
              <button key={m.id} onClick={() => onPick(m)}
                className="text-left" data-testid={`story-media-item-${m.id}`}>
                <div className="aspect-square rounded overflow-hidden bg-neutral-100">
                  <AuthenticatedEditorialImage url={src} alt="" className="w-full h-full object-cover" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- ImageSlot (same UX as HERO) ----------
function ImageSlot({ label, url, focalX, focalY, zoom, onFile, onClear, onOpenLibrary, onOpenCrop, testid }) {
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const m = await uploadDirect(f); onFile(m); toast.success("Фото загружено"); }
    catch (err) { toast.error("Не удалось: " + (err?.response?.data?.detail || err.message)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-[11px] leading-snug text-espresso/60">
        Рамка показывает итоговый кадр 4:5. Фото не растягивается; результат можно изменить через «Настроить кадр».
      </div>
      {url ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="mb-1 text-[10px] text-espresso/55">Исходное фото</div>
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg bg-neutral-100"
                data-testid={`${testid}-source-preview`}>
                <AuthenticatedEditorialImage url={url} alt="" className="h-full w-full object-contain pointer-events-none" />
              </div>
            </div>
            <div>
              <div className="mb-1 text-[10px] text-espresso/55">Кадр публикации</div>
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg bg-neutral-100" data-testid={`${testid}-preview`}>
                <AuthenticatedEditorialImage url={url} alt=""
                  className="w-full h-full object-cover pointer-events-none"
                  style={{
                    objectPosition: `${(focalX ?? 0.5) * 100}% ${(focalY ?? 0.5) * 100}%`,
                    transform: `scale(${zoom || 1})`,
                    transformOrigin: `${(focalX ?? 0.5) * 100}% ${(focalY ?? 0.5) * 100}%`,
                  }} />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-espresso/25 text-[12px]">
              <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
              <Upload className="w-3.5 h-3.5" /> {busy ? "Загрузка…" : "Заменить"}
            </label>
            <button onClick={onOpenLibrary} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-espresso/25 text-[12px]" data-testid={`${testid}-library`}>
              <ImageIcon className="w-3.5 h-3.5" /> Из Медиатеки
            </button>
            <button onClick={onOpenCrop} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-espresso/25 text-[12px]" data-testid={`${testid}-crop`}>
              <Crop className="w-3.5 h-3.5" /> Настроить кадр
            </button>
            {onClear && <button onClick={onClear} className="text-[12px] opacity-60" data-testid={`${testid}-clear`}>Убрать</button>}
          </div>
        </>
      ) : (
        <div className="rounded-lg bg-cream/80 border border-dashed border-espresso/25 aspect-[4/5] flex items-center justify-center">
          <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-espresso text-cream text-[12px]">
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
            <Upload className="w-3.5 h-3.5" /> {busy ? "Загрузка…" : "Загрузить с устройства"}
          </label>
        </div>
      )}
    </div>
  );
}

// ============================================================
//   EDITOR
// ============================================================
export function EditorialStoryEditor({
  block,
  onPatch,
  mode = "content",
  detailedOpen = false,
  onRequestDetailed,
  onReturnToCollected,
  onRequestPreview,
}) {
  const c = block.content || {};
  const s = block.settings || {};
  const paragraphs = (c.extra && Array.isArray(c.extra.paragraphs)) ? c.extra.paragraphs : [];
  const [libraryFor, setLibraryFor] = useState(null);
  const [cropFor, setCropFor] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [sourceText, setSourceText] = useState("");
  const [structuredNotice, setStructuredNotice] = useState(false);
  const [quickUploadBusy, setQuickUploadBusy] = useState(false);
  const desktopMediaSrc = useEditorialMediaUrl(c.hero_url, c.hero_media_id);
  const mobileMediaSrc = useEditorialMediaUrl(c.hero_mobile_url, c.hero_mobile_media_id);
  const hasContent = Boolean(c.headline || c.subtitle || c.lead || c.quote || paragraphs.some(Boolean));

  const patchContent = (patch) => onPatch({ content: { ...c, ...patch } });
  const patchSettings = (patch) => onPatch({ settings: { ...s, ...patch } });
  const setParagraphs = (p) => patchContent({ extra: { ...(c.extra || {}), paragraphs: p } });

  const addPar = () => setParagraphs([...paragraphs, ""]);
  const setPar = (i, v) => { const a = paragraphs.slice(); a[i] = v; setParagraphs(a); };
  const delPar = (i) => setParagraphs(paragraphs.filter((_, k) => k !== i));
  const movePar = (i, d) => {
    const j = i + d; if (j < 0 || j >= paragraphs.length) return;
    const a = paragraphs.slice(); [a[i], a[j]] = [a[j], a[i]]; setParagraphs(a);
  };

  const variant = s.variant || "A";
  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    if (cropFor === "desktop") patchSettings({ focal_x, focal_y, hero_zoom: zoom });
    else patchSettings({ mobile_focal_x: focal_x, mobile_focal_y: focal_y, hero_mobile_zoom: zoom });
    setCropFor(null);
  };
  const acceptMedia = (m, slot) => {
    if (slot === "desktop") patchContent({ hero_media_id: m.id, hero_url: m.file_url });
    else patchContent({ hero_mobile_media_id: m.id, hero_mobile_url: m.file_url });
    setLibraryFor(null);
  };

  const addPrimaryPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setQuickUploadBusy(true);
    try {
      const media = await uploadDirect(file);
      patchContent({ hero_media_id: media.id, hero_url: media.file_url });
      toast.success("Фото добавлено");
    } catch (error) {
      toast.error("Не удалось добавить фото: " + (error?.response?.data?.detail || error.message));
    } finally {
      setQuickUploadBusy(false);
      event.target.value = "";
    }
  };

  const structureText = () => {
    const result = structureEditorialStory(sourceText);
    if (!result.headline && !result.paragraphs.length) {
      toast.error("Вставьте текст материала");
      return;
    }
    patchContent({
      headline: result.headline,
      subtitle: result.subtitle,
      lead: result.lead,
      quote: result.quote,
      extra: { ...(c.extra || {}), paragraphs: result.paragraphs },
    });
    onReturnToCollected?.();
    setPasteOpen(false);
    setStructuredNotice(true);
    toast.success("Текст разложен по структуре");
  };

  return (
    <div className="space-y-3" data-testid="story-editor">
      {mode === "content" && !detailedOpen && (
      <div className="rounded-2xl border hairline bg-cream/55 p-4" data-testid="story-simple-entry">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-serif text-xl text-espresso">Редакционная история</div>
            <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-espresso/65">
              Принесите материал целиком и добавьте фотографию. Система подготовит структуру, которую можно проверить и исправить.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setPasteOpen(true)}
              className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream"
              data-testid="story-paste-open">
              Вставить текст
            </button>
            <label className="cursor-pointer rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
              data-testid="story-primary-photo">
              <input type="file" accept="image/*" className="hidden" onChange={addPrimaryPhoto}
                data-testid="story-primary-photo-file" />
              <Upload className="mr-1.5 inline h-4 w-4" />
              {quickUploadBusy ? "Загрузка…" : "Добавить фотографию"}
            </label>
            <button type="button" onClick={() => setLibraryFor("desktop")}
              className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
              data-testid="story-primary-library">
              <ImageIcon className="mr-1.5 inline h-4 w-4" /> Из Медиатеки
            </button>
            {!detailedOpen && (
              <button type="button" onClick={onRequestDetailed}
                className="px-2 py-2 text-sm text-espresso/65 underline decoration-espresso/25 underline-offset-4 hover:text-espresso"
                data-testid="story-manual-open">
                Заполнить вручную
              </button>
            )}
          </div>
        </div>
        {structuredNotice && !pasteOpen && (
          <p className="mt-3 text-[12px] text-olive" data-testid="story-structured-notice">
            Текст разложен по структуре. Проверьте результат — любой элемент можно изменить.
          </p>
        )}
        {c.hero_url && (
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-espresso/10 bg-white/70 p-2.5"
            data-testid="story-primary-photo-summary">
            <AuthenticatedEditorialImage url={c.hero_url} alt="" className="h-16 w-12 rounded object-cover" />
            <div className="text-[12px] text-espresso/65">Фотография добавлена. Кадрирование доступно в режиме «Оформление».</div>
          </div>
        )}
      </div>
      )}

      {!detailedOpen && pasteOpen && (
        <EditorialPilotDialog title="Вставить текст" onClose={() => setPasteOpen(false)}
          testid="story-paste-dialog">
          <label className="block text-sm text-espresso/75" htmlFor="story-paste-text">
            Вставьте материал целиком
          </label>
          <textarea id="story-paste-text" rows={15} value={sourceText}
            onChange={(event) => setSourceText(event.target.value)}
            className="mt-2 w-full rounded-xl border border-espresso/20 bg-white p-4 text-sm leading-relaxed outline-none focus:border-espresso/45"
            placeholder="Заголовок, вступление и весь основной текст"
            data-testid="story-paste-text" />
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setPasteOpen(false)}
              className="rounded-xl border border-espresso/20 px-4 py-2 text-sm">Отмена</button>
            <button type="button" onClick={structureText}
              className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream"
              data-testid="story-structure-submit">Разобрать материал</button>
          </div>
        </EditorialPilotDialog>
      )}

      {mode === "content" && hasContent && !detailedOpen && (
        <div className="rounded-2xl border border-espresso/10 bg-white p-5" data-testid="story-collected-result">
          <div className="text-[10px] uppercase tracking-[0.18em] text-espresso/50">Собранный материал</div>
          <div className="mt-4 grid gap-5 md:grid-cols-[minmax(0,1fr)_180px]">
            <div>
              {c.eyebrow && <div className="text-[10px] uppercase tracking-[0.2em] text-olive">{c.eyebrow}</div>}
              <h3 className="mt-1 font-serif text-3xl leading-tight text-espresso">{c.headline || "Без заголовка"}</h3>
              {c.subtitle && <p className="mt-2 font-serif text-lg italic text-espresso/75">{c.subtitle}</p>}
              {c.lead && <p className="mt-4 text-sm italic leading-relaxed text-espresso/75">{c.lead}</p>}
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-espresso/80">
                {paragraphs.filter(Boolean).map((paragraph, index) => <EditorialStoryFormattedParagraphV1 key={index} text={paragraph} />)}
              </div>
              {c.quote && <blockquote className="mt-5 border-l-2 border-olive pl-4 font-serif text-lg italic">{c.quote}</blockquote>}
            </div>
            {c.hero_url && <AuthenticatedEditorialImage url={c.hero_url} alt={c.alt || ""} className="h-auto w-full rounded-xl object-contain" />}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button type="button" onClick={onRequestDetailed}
              className="rounded-xl border border-espresso/20 px-4 py-2 text-sm text-espresso"
              data-testid="story-detailed-open">Редактировать подробно</button>
            <button type="button" onClick={onRequestPreview}
              className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream"
              data-testid="story-preview-open">Предпросмотр</button>
          </div>
        </div>
      )}

      {(detailedOpen || mode === "design") && (
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4" data-testid="story-structured-result">
      {/* CONTENT */}
      {mode === "content" && <div className="rounded-2xl bg-white border hairline p-4 space-y-4 xl:col-span-2">
        <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Предложенная структура</div>
        </div>
        <Input value={c.eyebrow || ""} onChange={(e) => patchContent({ eyebrow: e.target.value })}
          placeholder="Надзаголовок" data-testid="story-field-eyebrow" />
        <Input value={c.headline || ""} onChange={(e) => patchContent({ headline: e.target.value })}
          placeholder="Заголовок" className="font-serif text-lg" data-testid="story-field-headline" />
        <Input value={c.subtitle || ""} onChange={(e) => patchContent({ subtitle: e.target.value })}
          placeholder="Подзаголовок" data-testid="story-field-subtitle" />
        <textarea value={c.lead || ""} rows={2}
          onChange={(e) => patchContent({ lead: e.target.value })}
          className="w-full border rounded px-2 py-1.5 text-sm italic"
          placeholder="Короткое вступление"
          data-testid="story-field-lead" />

        <div className="pt-2">
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Основной текст</div>
          {paragraphs.map((p, i) => (
            <div key={i} className="border rounded p-2 mb-2" data-testid={`story-paragraph-${i}`}>
              <textarea value={p} rows={3}
                onChange={(e) => setPar(i, e.target.value)}
                className="w-full text-sm resize-y outline-none"
                placeholder={`Абзац ${i + 1}`} />
              <div className="flex items-center gap-1 mt-1 text-xs opacity-70">
                <button onClick={() => movePar(i, -1)} data-testid={`story-par-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => movePar(i, +1)} data-testid={`story-par-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => delPar(i)} className="text-red-600 ml-auto" data-testid={`story-par-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          <button onClick={addPar} className="w-full py-2 rounded border border-dashed border-espresso/25 text-sm text-espresso"
            data-testid="story-add-paragraph">
            <Plus className="w-4 h-4 inline mr-1" /> Добавить абзац
          </button>
        </div>

        <div className="pt-2">
          <label className="block text-[10px] uppercase tracking-widest opacity-70 mb-1">Акцентная цитата</label>
          <textarea value={c.quote || ""} rows={3}
            onChange={(e) => patchContent({ quote: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-sm font-serif italic"
            placeholder="Выделенная редакционная фраза"
            data-testid="story-field-quote" />
        </div>
        <Input value={c.caption || ""} onChange={(e) => patchContent({ caption: e.target.value })}
          placeholder="Подпись к фото" data-testid="story-field-caption" />
        <Input value={c.alt || ""} onChange={(e) => patchContent({ alt: e.target.value })}
          placeholder="Описание изображения · необязательно" data-testid="story-field-alt" />
        <p className="text-[11px] text-espresso/60 md:col-span-2">Кратко опишите фотографию для доступности. Текст не показывается поверх изображения.</p>
      </div>}

      {/* IMAGES + APPEARANCE */}
      {mode === "design" && <div className="space-y-4 xl:col-span-2" data-testid="story-design-layer">
        <div className="rounded-2xl bg-white border hairline p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Изображения</div>
          <ImageSlot label="Основное фото" url={c.hero_url}
            focalX={s.focal_x} focalY={s.focal_y} zoom={s.hero_zoom}
            onFile={(m) => patchContent({ hero_media_id: m.id, hero_url: m.file_url })}
            onClear={() => patchContent({ hero_media_id: null, hero_url: null })}
            onOpenLibrary={() => setLibraryFor("desktop")}
            onOpenCrop={() => setCropFor("desktop")}
            testid="story-image-desktop" />
          <ImageSlot label="Фото для телефона · пока не выбрано отдельное, используется основное" url={c.hero_mobile_url || c.hero_url}
            focalX={s.mobile_focal_x} focalY={s.mobile_focal_y} zoom={s.hero_mobile_zoom}
            onFile={(m) => patchContent({ hero_mobile_media_id: m.id, hero_mobile_url: m.file_url })}
            onClear={() => patchContent({ hero_mobile_media_id: null, hero_mobile_url: null })}
            onOpenLibrary={() => setLibraryFor("mobile")}
            onOpenCrop={() => setCropFor("mobile")}
            testid="story-image-mobile" />
        </div>

        <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Оформление</div>
          <div>
            <div className="text-[11px] opacity-70 mb-2">Вариант композиции</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "A", label: "Фото слева / текст справа" },
                { key: "B", label: "Текст слева / фото справа" },
              ].map((v) => (
                <button key={v.key} onClick={() => patchSettings({ variant: v.key })}
                  className={`p-3 rounded-xl border text-left ${variant === v.key ? "border-espresso bg-espresso/5" : "border-espresso/15"}`}
                  data-testid={`story-variant-${v.key}`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Вариант {v.key}</div>
                  <div className="text-[11px] mt-1">{v.label}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] opacity-70 mb-2">Акцентный цвет</div>
            <div className="flex flex-wrap gap-2">
              {HERO_PALETTES.map((p) => (
                <button key={p.key} onClick={() => patchSettings({ palette: p.key })}
                  className={`w-7 h-7 rounded-full border-2 ${s.palette === p.key ? "border-espresso" : "border-white shadow"}`}
                  style={{ background: p.swatch }} title={p.label}
                  data-testid={`story-palette-${p.key}`} />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] opacity-70 mb-1">Фон</label>
            <select value={s.background || "warm-ivory"}
              onChange={(e) => patchSettings({ background: e.target.value })}
              className="w-full h-10 rounded-md border border-espresso/25 bg-white px-3 text-sm outline-none"
              data-testid="story-background">
              {HERO_BACKGROUNDS.filter(b => b.key !== "full-image").map((b) => (
                <option key={b.key} value={b.key}>{b.label}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={!!s.botanical}
              onChange={(e) => patchSettings({ botanical: e.target.checked })}
              data-testid="story-botanical" />
            <span>Ботаническая декоративная деталь</span>
          </label>
        </div>
      </div>}

      </div>
      )}
      <MediaPicker open={!!libraryFor} onClose={() => setLibraryFor(null)}
        onPick={(m) => acceptMedia(m, libraryFor)} />
      <CropModal open={!!cropFor}
        mode={cropFor || "desktop"}
        url={cropFor === "mobile" ? (mobileMediaSrc || desktopMediaSrc) : desktopMediaSrc}
        focalX={cropFor === "mobile" ? (s.mobile_focal_x ?? 0.5) : (s.focal_x ?? 0.5)}
        focalY={cropFor === "mobile" ? (s.mobile_focal_y ?? 0.5) : (s.focal_y ?? 0.5)}
        zoom={cropFor === "mobile" ? (s.hero_mobile_zoom || 1) : (s.hero_zoom || 1)}
        onSave={saveCrop} onClose={() => setCropFor(null)} />
    </div>
  );
}

// ============================================================
//   RENDER — reader-facing composition
// ============================================================
export function EditorialStoryRender({ block, mode = "desktop", publicReader = false }) {
  const c = block.content || {};
  const s = block.settings || {};
  const variant = s.variant || "A";
  const palette = paletteByKey(s.palette);
  const background = backgroundByKey(s.background);
  const paragraphs = (c.extra?.paragraphs || []).filter(Boolean);
  const bg = background.css === "transparent" ? "#EFE6D5" : background.css;
  const textOnDark = background.key === "deep-cocoa";
  const primary = textOnDark ? "#F1E4CB" : "#3B241A";

  const desktopSrc = useEditorialMediaUrl(c.hero_url, c.hero_media_id, { publicReader });
  const mobileOwnSrc = useEditorialMediaUrl(c.hero_mobile_url, c.hero_mobile_media_id, { publicReader });
  const mobileSrc = mobileOwnSrc || desktopSrc;

  const fx = (s.focal_x ?? 0.5) * 100, fy = (s.focal_y ?? 0.5) * 100;
  const mfx = (s.mobile_focal_x ?? s.focal_x ?? 0.5) * 100;
  const mfy = (s.mobile_focal_y ?? s.focal_y ?? 0.5) * 100;
  const dz = s.hero_zoom || 1, mz = s.hero_mobile_zoom || 1;
  const desktopMedia = { focal_x: fx / 100, focal_y: fy / 100, zoom: dz };
  const mobileMedia = { focal_x: mfx / 100, focal_y: mfy / 100, zoom: mz };
  const desktopHasCrop = hasExplicitEditorialCrop(desktopMedia);
  const mobileHasCrop = hasExplicitEditorialCrop(mobileMedia);

  // ---------- MOBILE ----------
  if (mode === "mobile") {
    return (
      <section className="w-full overflow-hidden px-7 py-10" style={{ background: bg, color: primary }}
               data-testid="story-render-mobile" data-variant={variant}>
        {c.eyebrow && (
          <div className="text-[10px] tracking-[0.42em] uppercase mb-3 flex items-center gap-2"
               style={{ color: palette.swatch }}>
            <span className="h-px w-6" style={{ background: palette.swatch }} />
            {c.eyebrow}
          </div>
        )}
        <h1 className="min-w-0 break-words font-serif font-normal leading-[1.08] [overflow-wrap:anywhere]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(26px, 7vw, 30px)" }}>
          {c.headline || ""}
        </h1>
        {c.subtitle && <div className="mt-3 text-[14px] italic leading-[1.5] opacity-80">{c.subtitle}</div>}
        {c.lead && <p className="mt-5 text-[14px] italic leading-[1.7] opacity-85">{c.lead}</p>}
        {mobileSrc && (
          <figure className={`relative mx-auto mt-6 flex w-full max-w-[320px] justify-center overflow-hidden ${mobileHasCrop ? "aspect-[4/5] bg-[#E7DDCC]" : ""}`}
                  data-testid="story-mobile-image-frame">
            <img src={mobileSrc} alt={c.alt || ""}
              className={mobileHasCrop ? "absolute inset-0 h-full w-full" : "block h-auto max-h-[310px] w-auto max-w-full"}
              style={editorialImageStyle(mobileMedia)} />
            {c.caption && (
              <figcaption className="absolute bottom-3 left-3 bg-white/80 px-2 py-1 text-[10px] italic text-[#3B241A]">
                {c.caption}
              </figcaption>
            )}
          </figure>
        )}
        {paragraphs.length > 0 && (
          <div className="mt-6 space-y-4 text-[14px] leading-[1.8]">
            {paragraphs.map((p, i) => <EditorialStoryFormattedParagraphV1 key={i} text={p} />)}
          </div>
        )}
        {c.quote && (
          <blockquote className="mt-7 border-l pl-4 font-serif text-[17px] italic leading-[1.45]"
                      style={{ borderColor: palette.swatch, color: primary }}>
            {c.quote}
          </blockquote>
        )}
      </section>
    );
  }

  // ---------- DESKTOP ----------
  const ImgCol = (
    <div className="flex min-w-0 items-start justify-center px-8 py-12 lg:px-10 lg:py-14">
      <figure className={`relative flex w-full max-w-[420px] items-start justify-center overflow-hidden ${desktopHasCrop ? "aspect-[4/5] bg-[#E7DDCC]" : ""}`}
           data-testid="story-desktop-image-frame">
        {desktopSrc && (
          <img src={desktopSrc} alt={c.alt || ""}
            className={desktopHasCrop ? "absolute inset-0 h-full w-full" : "block h-auto max-h-[520px] w-auto max-w-full"}
            style={editorialImageStyle(desktopMedia)} />
        )}
        {c.caption && (
          <figcaption className="absolute bottom-4 left-4 px-2 py-1 text-[10px] italic"
               style={{ background: "rgba(255,255,255,0.78)", color: "#3B241A" }}>{c.caption}</figcaption>
        )}
      </figure>
    </div>
  );
  const TextCol = (
    <div className="min-w-0 px-8 py-12 lg:px-10 lg:py-14" style={{ color: primary }}>
      {c.eyebrow && (
        <div className="mb-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.44em]"
             style={{ color: palette.swatch }}>
          <span className="h-px w-8" style={{ background: palette.swatch }} />
          {c.eyebrow}
        </div>
      )}
      <h1 className="min-w-0 break-words font-serif font-normal leading-[1.08] [overflow-wrap:anywhere]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(30px, 3vw, 40px)" }}
          data-testid="story-render-headline">
        {c.headline || ""}
      </h1>
      {c.subtitle && <div className="mt-4 max-w-lg text-[16px] italic leading-[1.45] opacity-80">{c.subtitle}</div>}
      <div className="mt-5 flex items-center gap-2">
        <span className="h-px w-10" style={{ background: palette.swatch }} />
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: palette.swatch }} />
      </div>
      {c.lead && (
        <p className="mt-6 max-w-lg font-serif text-[18px] italic leading-[1.5]">
          {c.lead}
        </p>
      )}
      {paragraphs.length > 0 && (
        <div className="mt-6 max-w-[54ch] space-y-4 text-[14px] leading-[1.8]">
          {paragraphs.map((p, i) => <EditorialStoryFormattedParagraphV1 key={i} text={p} />)}
        </div>
      )}
      {c.quote && (
        <blockquote className="mt-8 max-w-lg border-l pl-5 font-serif"
                    style={{ borderColor: palette.swatch }}>
          <div className="text-[20px] italic leading-[1.4]" style={{ color: primary }}>{c.quote}</div>
        </blockquote>
      )}
    </div>
  );

  if (!desktopSrc) {
    return (
      <section style={{ background: bg }} data-testid="story-render-desktop" data-variant={variant}>
        <div className="mx-auto max-w-[700px]">{TextCol}</div>
      </section>
    );
  }

  return (
    <section className="mx-auto grid w-full max-w-[1120px] grid-cols-1 items-start overflow-hidden lg:grid-cols-[46%_54%]"
             style={{ background: bg }}
             data-testid="story-render-desktop" data-variant={variant}>
      {variant === "A" ? (<>{ImgCol}{TextCol}</>) : (<>{TextCol}{ImgCol}</>)}
    </section>
  );
}
